import { useCallback, useEffect } from "react";

import { registerTokenRenewer } from "../../queryClient";
import {
  renewedRecently,
  renewToken,
} from "../../services/auth/oidcManagerService";
import { postMessageToApp } from "../utils/hybridAppHelpers";

// Wires token lifecycle events on the OIDC UserManager.
// - accessTokenExpiring: attempts silent renewal. Unconditionally under Entra;
//   under ForgeRock only if the user has been active — see the note on
//   renewToken for why the two providers differ.
// - accessTokenExpired: no-op here; AuthSessionProvider's evaluateSession
//   detects the expired token and handles cleanup + redirect.
// - also registers the renewer the global QueryClient calls when a query hits
//   a 401 (see src/queryClient.js), so a cold-tab/expired-token blank
//   self-heals instead of sticking. A 401 right after a renewal is skipped (the
//   retry carries the new token), and the idle gate applies there too: a 401
//   after a long absence is a session to re-establish at the provider, not one
//   to quietly extend.
export const useAuthTokenLifecycle = (userManager, lastActivityRef) => {
  const handleRenewToken = useCallback(async () => {
    // In the mobile WebView the native app owns the session: it injects the
    // tokens and refreshes them itself. Renewing here would compete with it,
    // so ask native to refresh instead.
    if (globalThis.ReactNativeWebView) {
      postMessageToApp("TokenRefreshNeeded", "");
      return;
    }
    await renewToken(userManager, lastActivityRef?.current);
  }, [userManager, lastActivityRef]);

  // A 401 from a request sent before a renewal finished must not spend a second
  // refresh grant; the query's retry picks up the already-fresh token.
  const handleAuthErrorRenew = useCallback(async () => {
    if (!globalThis.ReactNativeWebView && renewedRecently(userManager)) {
      return;
    }
    await handleRenewToken();
  }, [userManager, handleRenewToken]);

  useEffect(() => {
    if (userManager) {
      userManager.events.addAccessTokenExpiring(handleRenewToken);
    }

    return () => {
      if (userManager) {
        userManager.events.removeAccessTokenExpiring(handleRenewToken);
      }
    };
  }, [userManager, handleRenewToken]);

  // Let the global QueryClient trigger the same renewal on a 401 from any
  // query. Registered once the UserManager exists; cleared on unmount.
  useEffect(() => {
    if (!userManager) return undefined;
    registerTokenRenewer(handleAuthErrorRenew);
    return () => registerTokenRenewer(null);
  }, [userManager, handleAuthErrorRenew]);
};
